// The quiz OPENING ARC (expectation question, path-reveal splash, reframed
// goal question, adaptive "path is taking shape" splash, bending goal chart).
// Built as web33_quiz_opening_arc (organic-only, Aug 5 – Sep 9 2026, decided).
//
// web39_paid_opening_arc (Sep 2026): the arc as a PAID-traffic test. Eligible =
// paid-ad traffic (utm_medium=paid or an fb* campaign) that landed on the
// already_know_why_main angle — Ethan's current paid landing page — and is not
// an LP experiment visitor (bws-lp handoffs, e.g. kelly_funnel). Variation =
// the arc instead of that angle's bridge screen + intro sequence; control = the
// bridge LP exactly as today. Everyone else gets OPENING_ARC_ORGANIC_DEFAULT
// (organic) or the plain opening (other paid traffic).
// Teardown: delete this folder and remove its App.tsx, chart, analytics,
// debug, style, and version call sites.

import { loc } from "../../i18n/survey";
import { GoodHandsSplashHeader } from "../../static/goodHandsSplash";
import { locHtml } from "../../static/locHtml";
import { isEligibleForPaidTests } from "../../utils";
import { pathRevealSplashElement } from "./pathRevealSplash";
import { pathTakingShapeSplashElement } from "./pathSplash";

export const WEB39_EXPERIMENT_NAME = "web39_paid_opening_arc";
export const WEB39_ARM_VARIATION = "variation";
export const WEB39_ARM_CONTROL = "control";
export const WEB39_ANGLE = "already_know_why_main";

// What ORGANIC (non-paid) visitors get now that web33 is over. true since
// 2026-09-18 (Steven: web33 was a success — the arc is the organic baseline).
// Paid traffic is untouched: web39 keeps testing it on its angle, other paid
// angles keep the plain opening.
export const OPENING_ARC_ORGANIC_DEFAULT = true;

export function isWeb39Eligible(utmAngle: string | null | undefined): boolean {
  return utmAngle === WEB39_ANGLE && isEligibleForPaidTests();
}

type QuizPage = {
  name: string;
  elements?: readonly unknown[];
  props?: Record<string, unknown>;
  [key: string]: unknown;
};

const chartThumbnail = (path: string, t: (key: string) => string) => `
  <svg class="web33-expectation-chart" viewBox="0 0 88 56" width="88" height="56" aria-hidden="true">
    <rect x="0.5" y="0.5" width="87" height="55" rx="7.5" fill="white" stroke="#E2E7EF" />
    <path d="M12 38.5 H76 M12 38.5 V10" fill="none" stroke="#CDD5E0" stroke-width="1" />
    <path d="${path}" fill="none" stroke="#406EDF" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
    <text x="10" y="50" fill="#8A929F" font-size="6">${t("splash.openingArc.expectation.chartToday")}</text>
    <text x="68" y="50" fill="#8A929F" font-size="6">${t("splash.openingArc.expectation.chartGoal")}</text>
  </svg>
`;

const transformationExpectationQuestion = {
  name: "transformationExpectation",
  title: loc("splash.openingArc.expectation.title"),
  description: loc("splash.openingArc.expectation.description"),
  descriptionLocation: "underTitle",
  type: "radiogroup",
  choices: [
    {
      value: "steady",
      text: locHtml((t) => `<div class="icon-button web33-expectation-choice">${chartThumbnail("M12 38 L76 12", t)}<div><b>${t("splash.openingArc.expectation.choices.steady")}</b></div></div>`),
    },
    {
      value: "fast_then_slow",
      text: locHtml((t) => `<div class="icon-button web33-expectation-choice">${chartThumbnail("M12 38 C26 19 39 13 76 12", t)}<div><b>${t("splash.openingArc.expectation.choices.fastThenSlow")}</b></div></div>`),
    },
    {
      value: "ups_and_downs",
      text: locHtml((t) => `<div class="icon-button web33-expectation-choice">${chartThumbnail("M12 38 C23 31 25 19 35 23 C46 28 48 12 58 18 C66 22 69 13 76 11", t)}<div><b>${t("splash.openingArc.expectation.choices.upsAndDowns")}</b></div></div>`),
    },
  ],
  isRequired: true,
};

const transformationExpectationPage: QuizPage = {
  name: "transformationExpectation",
  elements: [transformationExpectationQuestion],
};

const pathRevealSplashPage: QuizPage = {
  name: "pathRevealSplash",
  elements: [pathRevealSplashElement],
  props: {
    showHeader: false,
    showCurrentSection: false,
    nextButtonText: loc("splash.openingArc.pathReveal.nextButton"),
    customHeader: GoodHandsSplashHeader,
  },
};

export function buildOpeningArcPages<T extends QuizPage>(
  pages: readonly T[],
  enabled: boolean,
): readonly (T | QuizPage)[] {
  if (!enabled) return pages;

  const pageNames = new Set(pages.map((page) => page.name));
  if (!pageNames.has("goals") || !pageNames.has("goodHandsSplash")) return pages;

  return pages.flatMap((page) => {
    if (page.name === "goals") {
      const elements = page.elements?.map((element, index) => {
        if (index !== 0 || typeof element !== "object" || element === null) {
          return element;
        }
        return {
          ...element,
          title: loc("splash.openingArc.goals.title"),
          description: loc("splash.openingArc.goals.description"),
        };
      });

      return [
        transformationExpectationPage,
        pathRevealSplashPage,
        { ...page, elements },
      ];
    }

    if (page.name === "goodHandsSplash") {
      return [{ ...page, elements: [pathTakingShapeSplashElement] }];
    }

    return [page];
  });
}
